import { useEffect, useState } from "react";

import { format } from "date-fns";
import { CalendarIcon, Clock, Loader2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { supabase } from "~/integrations/supabase/client";
import { cn } from "~/lib/utils";
import { SalesRequest } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

// Special value to represent "no selection"
const NONE_VALUE = "none";

interface SalesRequestSelectProps {
  clientId: string;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const SalesRequestSelect = ({
  clientId,
  value,
  onChange,
  disabled = false,
}: SalesRequestSelectProps) => {
  const [salesRequests, setSalesRequests] = useState<SalesRequest[]>([]);
  const [loading, setLoading] = useState(true);

  // Convert between actual value and internal component value
  const internalValue = value ? value : NONE_VALUE;

  const handleValueChange = (selectedValue: string) => {
    // Convert back from internal value to actual value
    onChange(selectedValue === NONE_VALUE ? "" : selectedValue);
  };

  // Fetch sales requests
  useEffect(() => {
    const fetchSalesRequests = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("sales_request")
          .select("*")
          .eq("client_id", clientId)
          .in("status", ["prepaid", "accepted", "in_transit", "delivered"]) // Include prepaid status for payment attachment
          .eq("paid", false) // Only show unpaid requests
          .order("created_at", { ascending: false });

        if (error) throw error;

        // Transform to frontend format
        const formattedRequests: SalesRequest[] = data.map((request) => ({
          id: request.id,
          clientId: request.client_id,
          createdBy: request.created_by,
          approvedRejectedBy: request.approved_rejected_by || undefined,
          status: request.status as
            | "pending"
            | "prepaid"
            | "accepted"
            | "rejected"
            | "in_transit"
            | "delivered"
            | "payment_to_check"
            | "test",
          paid: request.paid,
          paymentTerms: request.payment_terms,
          paymentType: request.payment_type as "future" | "prepaid",
          paymentDueDate: request.payment_due_date,
          totalAmount: request.total_amount,
          comments: request.comments || undefined,
          deliveryDate: request.delivery_date || undefined,
          createdAt: request.created_at,
        }));

        setSalesRequests(formattedRequests);
      } catch (err) {
        console.error("Error fetching sales requests:", err);
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchSalesRequests();
    }
  }, [clientId]);

  // Format display text for each sales request
  const formatSalesRequestLabel = (request: SalesRequest) => {
    const date = format(new Date(request.paymentDueDate), "dd/MM/yyyy");
    const amount = formatCurrency(request.totalAmount);
    const statusText = getStatusText(request.status);
    return `${date} - ${amount} - ${statusText}`;
  };

  // Get status text in Spanish
  const getStatusText = (status: SalesRequest["status"]) => {
    switch (status) {
      case "pending":
        return "Pendiente";
      case "accepted":
        return "Aceptada";
      case "rejected":
        return "Rechazada";
      case "in_transit":
        return "En tránsito";
      case "delivered":
        return "Entregada";
      default:
        return status;
    }
  };

  // Get status badge color
  const getStatusColor = (status: SalesRequest["status"]) => {
    switch (status) {
      case "accepted":
        return "bg-green-100 text-green-800 border-green-200";
      case "in_transit":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "delivered":
        return "bg-purple-100 text-purple-800 border-purple-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <Select
      value={internalValue}
      onValueChange={handleValueChange}
      disabled={disabled || loading}
    >
      <SelectTrigger className={loading ? "opacity-70" : ""}>
        {loading ? (
          <div className="flex items-center">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span>Cargando notas de venta...</span>
          </div>
        ) : (
          <SelectValue placeholder="Seleccionar nota de venta (recomendado)" />
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_VALUE}>
          <span className="text-gray-500">Ninguna</span>
        </SelectItem>

        {salesRequests.length === 0 && !loading ? (
          <div className="px-2 py-2 text-sm text-gray-500">
            No hay notas de venta pendientes de pago
          </div>
        ) : (
          salesRequests.map((request) => (
            <SelectItem key={request.id} value={request.id}>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm font-medium">
                      #{request.id.substring(0, 8)}
                    </span>
                    <Badge
                      variant="outline"
                      className={cn(
                        "ml-2 text-xs",
                        getStatusColor(request.status)
                      )}
                    >
                      {getStatusText(request.status)}
                    </Badge>
                  </div>
                  <span className="font-bold text-green-600">
                    {formatCurrency(request.totalAmount)}
                  </span>
                </div>
                <div className="flex items-center text-xs text-gray-500">
                  <CalendarIcon className="mr-1 h-3 w-3" />
                  <span>
                    Vence:{" "}
                    {format(new Date(request.paymentDueDate), "dd/MM/yyyy")}
                  </span>
                  {new Date(request.paymentDueDate) < new Date() && (
                    <Badge
                      variant="outline"
                      className="ml-2 bg-red-50 text-xs text-red-700"
                    >
                      <Clock className="mr-1 h-2 w-2" />
                      Vencida
                    </Badge>
                  )}
                </div>
              </div>
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
};
